
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface TitleConfig {
  text: string;
  background?: {
    type: 'image' | 'color';
    image?: {
      url: string;
    };
    color?: string;
  };
}

interface Settings {
  language: 'he' | 'en';
  rotationInterval: number;
  titleConfig: TitleConfig;
}

interface AppSetting {
  id: string;
  setting_key: string;
  setting_value: unknown;
  created_at: string;
  updated_at: string;
}

interface SettingsContextType {
  settings: Settings;
  loading: boolean;
  updateSettings: (newSettings: Partial<Settings>) => Promise<void>;
}

const defaultSettings: Settings = {
  language: 'he',
  rotationInterval: 30,
  titleConfig: {
    text: 'ברוכים הבאים',
    background: {
      type: 'color',
      color: '#ffffff'
    }
  }
};

const SettingsContext = createContext<SettingsContextType>({
  settings: defaultSettings,
  loading: true,
  updateSettings: async () => {},
});

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Use select() instead of single() to avoid 406 errors when no data is found
        const { data, error } = await supabase
          .from('app_settings')
          .select('*')
          .eq('setting_key', 'display_settings');

        if (error) throw error;

        if (data && data.length > 0) {
          const appSetting = data[0] as AppSetting;
          const settingsData = appSetting.setting_value as any;
          setSettings({
            ...defaultSettings,
            ...settingsData
          });
        } else {
          // No settings found, create default ones
          console.log("No settings found, using defaults");
          
          // Try to insert default settings
          try {
            const { error: insertError } = await supabase
              .from('app_settings')
              .insert({
                setting_key: 'display_settings',
                setting_value: defaultSettings as any
              });
              
            if (insertError) {
              console.error('Error creating default settings:', insertError);
            }
          } catch (insertErr) {
            console.error('Failed to create default settings:', insertErr);
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast({
          title: "Settings Error",
          description: "Could not load settings. Default settings will be used.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const updateSettings = async (newSettings: Partial<Settings>) => {
    try {
      const updatedSettings = {
        ...settings,
        ...newSettings
      };

      const { error } = await supabase
        .from('app_settings')
        .upsert({
          setting_key: 'display_settings',
          setting_value: updatedSettings as any
        });

      if (error) throw error;

      setSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Update Failed",
        description: "Could not update settings. Please try again.",
        variant: "destructive"
      });
      throw error;
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};
